import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { TnaMilestoneService } from '../../services/tnaMilestone.service';
import { CostingService } from '../../services/costing.service';
import { QcInspectionService } from '../../services/qcInspection.service';
import { StyleService } from '../../services/style.service';
import { BuyerService } from '../../services/buyer.service';
import { BuyerOrderStatus, IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { ITnaMilestone } from '../../interfaces/garments/tnaMilestone.interface';
import { IQcSummary } from '../../interfaces/garments/qcInspection.interface';
import { IStyle } from '../../interfaces/garments/style.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { OutputStage, ProductionOutput } from '../../interfaces/costing.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate } from '../../utils/dateUtils';
import Sparkline from '../dashboard/charts/Sparkline';
import HeroShell from '../hero/HeroShell';
import GarmentFill from '../hero/GarmentFill';
import SpoolGauge from '../hero/SpoolGauge';
import TrendChip from '../hero/TrendChip';
import OrderThread, { ThreadStep } from '../hero/OrderThread';
import { HERO_CARD_SX, SR_ONLY } from '../hero/heroStyles';

/*
 * Order Thread (decision note docs/decisions/2026-10-23-hero-asset-pages.md, phase 1a).
 * One buyer order as a hero page: the garment filling up, the T&A thread, and daily output.
 * Read-only. Each section shows only when the viewer holds its key, and it never shows money.
 */

const STATUS_COLORS: Record<BuyerOrderStatus, 'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'> = {
    DRAFT: 'default', CONFIRMED: 'info', IN_PRODUCTION: 'primary', SHIPPED: 'success', CLOSED: 'default', CANCELLED: 'error',
};

const STAGES: { stage: OutputStage; label: string }[] = [
    { stage: 'CUT', label: 'Cut' }, { stage: 'SEWN', label: 'Sewn' }, { stage: 'FINISHED', label: 'Finished' },
];
const SPARK_DAYS = 14;

const num = (n: number) => n.toLocaleString('en-US');

/** Local calendar date from a `YYYY-MM-DD` string (no UTC shift). */
const parseDay = (iso: string): Date => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};
const dayDiff = (fromIso: string, toIso: string): number => {
    const a = parseDay(fromIso); const b = parseDay(toIso);
    return Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000);
};
const shortDate = (iso?: string | null) =>
    iso ? parseDay(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
const longDate = (iso?: string | null) =>
    iso ? parseDay(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** T&A milestones as thread steps: done ones stitched, the first open one current, late by date. */
export function toThreadSteps(milestones: ITnaMilestone[], today: string): ThreadStep[] {
    const sorted = [...milestones].sort((a, b) => a.sequence - b.sequence);
    const firstOpen = sorted.findIndex(m => !m.completed);
    return sorted.map((m, i) => {
        if (m.completed) {
            const lateBy = m.actualDate && m.targetDate ? dayDiff(m.targetDate, m.actualDate) : 0;
            const done = m.actualDate ? `Done ${shortDate(m.actualDate)}` : 'Done';
            return {
                key: m.milestoneType, label: m.displayLabel, state: 'done',
                late: lateBy > 0, note: lateBy > 0 ? `${done} · ${lateBy} ${lateBy === 1 ? 'day' : 'days'} late` : done,
            };
        }
        const overdueBy = m.targetDate ? dayDiff(m.targetDate, today) : 0;
        return {
            key: m.milestoneType, label: m.displayLabel, state: i === firstOpen ? 'current' : 'todo',
            late: overdueBy > 0,
            note: m.targetDate
                ? (overdueBy > 0 ? `Due ${shortDate(m.targetDate)} · ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'} over` : `Due ${shortDate(m.targetDate)}`)
                : undefined,
        };
    });
}

interface Loaded {
    order: IBuyerOrder | null;
    orderError: boolean;
    tna: ITnaMilestone[];
    tnaError: boolean;
    outputs: ProductionOutput[] | null;
    outputsError: boolean;
    qc: IQcSummary | null;
    qcError: boolean;
    style: IStyle | null;
    buyer: IBuyer | null;
}

const EMPTY: Loaded = {
    order: null, orderError: false, tna: [], tnaError: false, outputs: null, outputsError: false,
    qc: null, qcError: false, style: null, buyer: null,
};

export default function OrderJourneyPage() {
    const { id = '' } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canOutput = hasPermission(PERMISSIONS.COSTING_VIEW);
    const canQc = hasPermission(PERMISSIONS.QC_INSPECTION_VIEW);
    const canStyle = hasPermission(PERMISSIONS.STYLE_VIEW);
    const canBuyer = hasPermission(PERMISSIONS.BUYER_VIEW);
    const institutionId = AuthService.getSelectedInstitution() ?? '';

    const [data, setData] = useState<Loaded>(EMPTY);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            setLoading(true);
            // all calls at once; a section the viewer cannot see is never asked for (no 403 toasts)
            const [orderR, tnaR, outR, qcR] = await Promise.allSettled([
                BuyerOrderService.getById(institutionId, id),
                TnaMilestoneService.list(institutionId, id),
                canOutput ? CostingService.listOutputs(institutionId, { buyerOrderId: id }) : Promise.resolve(null),
                canQc ? QcInspectionService.summary(institutionId, { buyerOrderId: id }) : Promise.resolve(null),
            ]);
            const order = orderR.status === 'fulfilled' ? orderR.value : null;
            // style and buyer names are nice-to-have: fetched only with their own keys, silent on failure
            const styleId = order?.lines?.[0]?.styleId;
            const [styleR, buyerR] = await Promise.allSettled([
                canStyle && styleId ? StyleService.getById(institutionId, styleId) : Promise.resolve(null),
                canBuyer && order?.buyerId ? BuyerService.getById(institutionId, order.buyerId) : Promise.resolve(null),
            ]);
            if (!alive) return;
            setData({
                order,
                orderError: orderR.status === 'rejected',
                tna: tnaR.status === 'fulfilled' ? tnaR.value : [],
                tnaError: tnaR.status === 'rejected',
                outputs: outR.status === 'fulfilled' ? outR.value : null,
                outputsError: outR.status === 'rejected',
                qc: qcR.status === 'fulfilled' ? qcR.value : null,
                qcError: qcR.status === 'rejected',
                style: styleR.status === 'fulfilled' ? styleR.value : null,
                buyer: buyerR.status === 'fulfilled' ? buyerR.value : null,
            });
            setLoading(false);
        };
        load();
        return () => { alive = false; };
    }, [id, institutionId, canOutput, canQc, canStyle, canBuyer]);

    const today = toLocalIsoDate();
    const yesterday = toLocalIsoDate(new Date(Date.now() - 86400000));

    const totals = useMemo(() => {
        const t: Record<OutputStage, number> = { CUT: 0, SEWN: 0, FINISHED: 0 };
        let finishedToday = 0; let finishedYesterday = 0;
        for (const o of data.outputs ?? []) {
            t[o.stage] += o.quantity;
            if (o.stage === 'FINISHED' && o.outputDate === today) finishedToday += o.quantity;
            if (o.stage === 'FINISHED' && o.outputDate === yesterday) finishedYesterday += o.quantity;
        }
        return { ...t, finishedToday, finishedYesterday };
    }, [data.outputs, today, yesterday]);

    const days = useMemo(() => {
        const out: string[] = [];
        for (let i = SPARK_DAYS - 1; i >= 0; i--) out.push(toLocalIsoDate(new Date(Date.now() - i * 86400000)));
        return out;
    }, []);
    const daily = useMemo(() => {
        const byStage: Record<OutputStage, number[]> = { CUT: days.map(() => 0), SEWN: days.map(() => 0), FINISHED: days.map(() => 0) };
        for (const o of data.outputs ?? []) {
            const idx = days.indexOf(o.outputDate.slice(0, 10));
            if (idx >= 0) byStage[o.stage][idx] += o.quantity;
        }
        return byStage;
    }, [data.outputs, days]);

    const steps = useMemo(() => toThreadSteps(data.tna, today), [data.tna, today]);

    if (loading) {
        return <Box sx={{ p: 3 }}><CircularProgress aria-label="Loading order" /></Box>;
    }

    const back = (
        <Button data-help="merchandising.journey.back" startIcon={<ArrowBackIcon />}
                onClick={() => navigate(id ? `/merchandising/orders/${id}` : '/merchandising/orders')}>
            Back to order
        </Button>
    );

    const { order } = data;
    if (!order) {
        return (
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }} data-help-page="merchandising.journey.page">
                {back}
                <Alert severity="error" sx={{ mt: 2 }}>Could not load this order. It may have been removed, or you may not have access to it.</Alert>
            </Box>
        );
    }

    const qty = order.totalQuantity ?? 0;
    const hasOutput = (data.outputs ?? []).length > 0;
    const share = (n: number) => (qty > 0 ? n / qty : 0);
    const finishedPct = canOutput && hasOutput && qty > 0 ? (totals.FINISHED / qty) * 100 : null;

    const exFactory = data.tna.find(m => m.milestoneType === 'EX_FACTORY');
    const exFactoryDate = exFactory?.targetDate ?? order.shipDate;
    const daysLeft = exFactoryDate ? dayDiff(today, exFactoryDate) : null;
    const lateSteps = steps.filter(s => s.late).length;

    const styleText = data.style ? `${data.style.styleNumber} · ${data.style.name}` : null;
    const subtitle = [data.buyer?.name, styleText].filter(Boolean).join(' · ') || undefined;

    const card = (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: '1 1 auto', overflowWrap: 'anywhere' }}>{order.orderNumber}</Typography>
                <Chip size="small" label={enumLabel(order.status)} color={STATUS_COLORS[order.status]} />
            </Box>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5, fontSize: 14 }}>
                {data.buyer && (<><Box component="dt" sx={{ color: 'text.secondary' }}>Buyer</Box><Box component="dd" sx={{ m: 0 }}>{data.buyer.name}</Box></>)}
                {order.buyerPoNumber && (<><Box component="dt" sx={{ color: 'text.secondary' }}>Buyer PO</Box><Box component="dd" sx={{ m: 0 }}>{order.buyerPoNumber}</Box></>)}
                {styleText && (<><Box component="dt" sx={{ color: 'text.secondary' }}>Style</Box><Box component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>{styleText}</Box></>)}
                <Box component="dt" sx={{ color: 'text.secondary' }}>Quantity</Box><Box component="dd" sx={{ m: 0 }}>{qty > 0 ? `${num(qty)} pcs` : '—'}</Box>
                <Box component="dt" sx={{ color: 'text.secondary' }}>Ordered</Box><Box component="dd" sx={{ m: 0 }}>{longDate(order.orderDate)}</Box>
                <Box component="dt" sx={{ color: 'text.secondary' }}>Ship date</Box><Box component="dd" sx={{ m: 0 }}>{longDate(order.shipDate)}</Box>
            </Box>
        </Box>
    );

    const visual = (
        <>
            <GarmentFill
                garmentType={data.style?.garmentType}
                cut={canOutput && hasOutput ? share(totals.CUT) : undefined}
                sewn={canOutput && hasOutput ? share(totals.SEWN) : undefined}
                finished={canOutput && hasOutput ? share(totals.FINISHED) : undefined}
                width={190}
            />
            {canOutput && (
                <SpoolGauge
                    value={finishedPct}
                    label="Finished"
                    caption={finishedPct != null ? `${num(totals.FINISHED)} of ${num(qty)} pcs` : undefined}
                />
            )}
        </>
    );

    const stageChip = (stage: OutputStage, label: string) => {
        if (!hasOutput) return <TrendChip key={stage} label={label} value="Not started" />;
        const v = totals[stage];
        const sub = qty > 0 ? `${Math.round((v / qty) * 100)}% of order` : undefined;
        if (stage === 'FINISHED') {
            return (
                <TrendChip key={stage} label={label} value={num(v)} sub={sub}
                           delta={{ current: totals.finishedToday, previous: totals.finishedYesterday, against: 'today vs yesterday' }} />
            );
        }
        return <TrendChip key={stage} label={label} value={num(v)} sub={sub} />;
    };

    let exValue = '—'; let exSub: string | undefined; let exTone: 'default' | 'warning' | 'critical' | 'good' = 'default';
    if (exFactory?.completed) {
        exValue = 'Done'; exSub = exFactory.actualDate ? `Left ${longDate(exFactory.actualDate)}` : undefined; exTone = 'good';
    } else if (daysLeft != null) {
        exValue = daysLeft < 0 ? `${num(-daysLeft)} over` : num(daysLeft);
        exSub = `${daysLeft < 0 ? 'Was due' : 'Due'} ${longDate(exFactoryDate)}${exFactory ? '' : ' (ship date)'}`;
        exTone = daysLeft < 0 ? 'critical' : daysLeft <= 7 ? 'warning' : 'default';
    }

    return (
        <HeroShell
            data-help-page="merchandising.journey.page"
            data-help-section="merchandising.journey.hero"
            title={`Order ${order.orderNumber}`}
            subtitle={subtitle}
            actions={back}
            visual={visual}
            card={card}
        >
            <Box data-help-section="merchandising.journey.kpis" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <TrendChip label="Ordered" value={qty > 0 ? num(qty) : '—'} sub={qty > 0 ? 'pieces' : undefined} />
                {canOutput && STAGES.map(s => stageChip(s.stage, s.label))}
                {canQc && (
                    data.qcError
                        ? <TrendChip label="QC pass rate" value="—" sub="Could not load" />
                        : <TrendChip
                            label="QC pass rate"
                            value={data.qc?.passRatePercent != null ? `${Math.round(data.qc.passRatePercent)}%` : '—'}
                            sub={data.qc?.passRatePercent != null ? `${num(data.qc.passed)} passed · ${num(data.qc.failed)} failed` : 'No inspections yet'}
                        />
                )}
                <TrendChip label="Days to ex-factory" value={exValue} sub={exSub} tone={exTone} />
            </Box>
            {canOutput && data.outputsError && (
                <Alert severity="warning" sx={{ mb: 2 }}>Could not load production output. The figures above leave it out.</Alert>
            )}

            <Box data-help-section="merchandising.journey.thread" sx={{ ...HERO_CARD_SX, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, mb: 2 }}>
                    <Typography variant="h6" component="h2" sx={{ flex: '1 1 auto' }}>Time and action</Typography>
                    {steps.length > 0 && (
                        <Typography variant="body2" color={lateSteps ? 'error.main' : 'text.secondary'}>
                            {lateSteps ? `${lateSteps} ${lateSteps === 1 ? 'step' : 'steps'} late` : 'On plan'}
                        </Typography>
                    )}
                </Box>
                {data.tnaError
                    ? <Alert severity="warning">Could not load the T&A calendar.</Alert>
                    : <OrderThread steps={steps} name={`Order ${order.orderNumber}`} emptyText="No T&A calendar yet. It is made when the order is confirmed." />}
            </Box>

            {canOutput && !data.outputsError && (
                <Box data-help-section="merchandising.journey.output" sx={{ ...HERO_CARD_SX, mb: 2 }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>Daily output, last {SPARK_DAYS} days</Typography>
                    {!hasOutput ? (
                        <Typography variant="body2" color="text.secondary" data-testid="journey-output-empty">
                            Not started. No cut, sewn or finished output is recorded for this order yet.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {STAGES.map(({ stage, label }) => {
                                const vals = daily[stage];
                                const sum = vals.reduce((a, b) => a + b, 0);
                                const todayVal = vals[vals.length - 1];
                                return (
                                    <Box key={stage} data-testid={`journey-spark-${stage}`}
                                         sx={{ flex: '1 1 180px', maxWidth: { xs: '100%', sm: 320 }, minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 3, p: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" aria-hidden>{label}</Typography>
                                        <Typography aria-hidden sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                            {num(sum)} <Typography component="span" variant="caption" color="text.secondary">pcs · today {num(todayVal)}</Typography>
                                        </Typography>
                                        <Box aria-hidden sx={{ mt: 0.5, '& svg': { width: '100%', height: 'auto', display: 'block' } }}>
                                            <Sparkline values={vals} width={280} height={40} />
                                        </Box>
                                        <Box component="span" sx={SR_ONLY}>
                                            {`${label}, last ${SPARK_DAYS} days: ${num(sum)} pieces, ${num(todayVal)} today, highest day ${num(Math.max(...vals))}.`}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </HeroShell>
    );
}
